// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/compras/types/index.ts
//  Tipos alinhados com os DTOs da API — módulo Compras (P2P).
// ════════════════════════════════════════════════════════════════════════════

import type { PaginadoResponse } from '@/features/operacional/types';
export type { PaginadoResponse };

/** Filtros das listas de Compras — tudo vai ao servidor (período = dia local yyyy-MM-dd). */
export interface FiltrosListaCompras {
  busca?: string;
  dtInicio?: string;
  dtFim?: string;
  ordenarPor?: string;
  decrescente?: boolean;
}

/** Lista paginada + resumo do conjunto filtrado (não só da página), para os cartões. */
export interface PaginadoComResumo<T> extends PaginadoResponse<T> {
  /** Quantidade por status (ignora o filtro de status). */
  porStatus?: Record<string, number>;
  /** Soma do valor total com todos os filtros. */
  valorTotal?: number;
  /** Cotações: quantas têm ao menos uma resposta de fornecedor. */
  comRespostas?: number;
}

/** Parâmetros de query comuns das listas (omite vazios). */
export const paramsListaCompras = (f: FiltrosListaCompras = {}) => ({
  busca: f.busca?.trim() || undefined,
  dtInicio: f.dtInicio || undefined,
  dtFim: f.dtFim || undefined,
  ordenarPor: f.ordenarPor || undefined,
  decrescente: f.decrescente,
});

// ── Enums ─────────────────────────────────────────────────────────────────

export type StatusRequisicaoCompra =
  | 'Rascunho'
  | 'Pendente'
  | 'EmAprovacao'
  | 'Aprovada'
  | 'Rejeitada'
  | 'Cancelada'
  | 'Atendida';

export type StatusOrdemCompra =
  | 'Rascunho'
  | 'Pendente'
  | 'EmAprovacao'
  | 'Aprovada'
  | 'EnviadaFornecedor'
  | 'ConfirmadaFornecedor'
  | 'ParcialmenteRecebida'
  | 'TotalmenteRecebida'
  | 'Cancelada';

export type StatusRecebimentoCompra =
  | 'Pendente'
  | 'ParcialmenteRecebido'
  | 'TotalmenteRecebido'
  | 'Cancelado';

// ── Fornecedor ────────────────────────────────────────────────────────────

export interface FornecedorResumo {
  id: number;
  nomeRazao: string;
  apelido?: string;
  cpfCnpj?: string;
  inscricaoEstadual?: string;
  email?: string;
  fone?: string;
  celular?: string;
  isAtivo: boolean;
  dtCriacao: string;
}

export interface FornecedorDetalhe extends FornecedorResumo {
  tipoPessoa?: string;
  observacao?: string;
}

export interface CriarFornecedorRequest {
  nomeRazao: string;
  apelido?: string;
  cpfCnpj?: string;
  inscricaoEstadual?: string;
  email?: string;
  fone?: string;
  celular?: string;
  tipoPessoa?: string;
  observacao?: string;
}

export interface AtualizarFornecedorRequest extends CriarFornecedorRequest {
  isAtivo: boolean;
}

// ── Requisição de Compra ──────────────────────────────────────────────────

export interface RequisicaoCompraItem {
  id: number;
  produtoId: number;
  descricaoComplementar?: string;
  quantidade: number;
  unidadeId?: number;
  custoEstimado: number;
  totalEstimado: number;
  fornecedorSugeridoId?: number;
}

export interface RequisicaoCompraResumo {
  id: number;
  numero: number;
  dtEmissao: string;
  dtNecessidade?: string;
  usuarioSolicitanteId: number;
  localEstoqueId?: number;
  justificativa?: string;
  status: StatusRequisicaoCompra;
  valorTotal: number;
  dtAprovacao?: string;
  usuarioAprovadorId?: number;
  dtCriacao: string;
}

export interface RequisicaoCompraDetalhe extends RequisicaoCompraResumo {
  observacao?: string;
  itens: RequisicaoCompraItem[];
}

export interface RequisicaoCompraItemRequest {
  produtoId: number;
  descricaoComplementar?: string;
  quantidade: number;
  unidadeId?: number;
  custoEstimado: number;
  fornecedorSugeridoId?: number;
}

export interface CriarRequisicaoCompraRequest {
  dtNecessidade?: string;
  localEstoqueId?: number;
  justificativa?: string;
  observacao?: string;
  itens: RequisicaoCompraItemRequest[];
}

export type AtualizarRequisicaoCompraRequest = CriarRequisicaoCompraRequest;

export interface AprovarRequisicaoCompraRequest {
  aprovado: boolean;
  justificativa?: string;
}

// ── Ordem de Compra ───────────────────────────────────────────────────────

export interface OrdemCompraItem {
  id: number;
  ordemId: number;
  produtoId: number;
  descricaoComplementar?: string;
  quantidade: number;
  quantidadeRecebida: number;
  unidadeId?: number;
  custoUnitario: number;
  descontoItem: number;
  totalItem: number;
  localEstoqueId?: number;
}

export interface OrdemCompraResumo {
  id: number;
  numero: number;
  dtEmissao: string;
  dtEntregaPrevista?: string;
  fornecedorId: number;
  nomeFornecedor?: string;
  requisicaoOrigemId?: number;
  valorTotal: number;
  status: StatusOrdemCompra;
  dtEnvioFornecedor?: string;
  dtCriacao: string;
}

export interface OrdemCompraDetalhe extends OrdemCompraResumo {
  localEstoqueId?: number;
  formaPagamentoId?: number;
  prazoPagamentoDias: number;
  frete: number;
  desconto: number;
  outrasDespesas: number;
  valorProdutos: number;
  condicoesPagamento?: string;
  observacao?: string;
  dtConfirmacaoFornecedor?: string;
  dtAprovacao?: string;
  usuarioAprovadorId?: number;
  itens: OrdemCompraItem[];
}

export interface OrdemCompraItemRequest {
  produtoId: number;
  descricaoComplementar?: string;
  quantidade: number;
  unidadeId?: number;
  custoUnitario: number;
  descontoItem: number;
  localEstoqueId?: number;
}

export interface CriarOrdemCompraRequest {
  fornecedorId: number;
  dtEntregaPrevista?: string;
  requisicaoOrigemId?: number;
  localEstoqueId?: number;
  formaPagamentoId?: number;
  prazoPagamentoDias: number;
  frete: number;
  desconto: number;
  outrasDespesas: number;
  condicoesPagamento?: string;
  observacao?: string;
  itens: OrdemCompraItemRequest[];
}

export interface AtualizarOrdemCompraRequest extends CriarOrdemCompraRequest {}

export interface AprovarOrdemCompraRequest {
  aprovado: boolean;
  justificativa?: string;
}

// ── Recebimento ───────────────────────────────────────────────────────────

export interface RecebimentoCompraItem {
  id: number;
  produtoId: number;
  quantidadeRecebida: number;
  custoUnitario: number;
  totalItem: number;
  localEstoqueId?: number;
  lote?: string;
  dtValidade?: string;
}

export interface RecebimentoCompraResumo {
  id: number;
  numero: number;
  /** null em recebimento avulso (sem ordem de compra vinculada) */
  ordemId?: number;
  dtRecebimento: string;
  fornecedorId: number;
  numeroNf?: string;
  valorTotal: number;
  status: StatusRecebimentoCompra;
  isThreeWayMatch: boolean;
  dtCriacao: string;
}

export interface RecebimentoCompraDetalhe extends RecebimentoCompraResumo {
  localEstoqueId: number;
  serieNf?: string;
  chaveNfe?: string;
  dtEmissaoNf?: string;
  valorNf: number;
  dtThreeWayMatch?: string;
  observacao?: string;
  contaPagarGeradaId?: number;
  itens: RecebimentoCompraItem[];
}

export interface RecebimentoCompraItemRequest {
  ordemItemId: number;
  produtoId: number;
  quantidadeRecebida: number;
  custoUnitario: number;
  localEstoqueId?: number;
  lote?: string;
  dtValidade?: string;
}

// ── Alçada de Aprovação ───────────────────────────────────────────────────

export type TipoAlcadaCompra = 'ValorAte' | 'ValorIlimitado';

export interface AlcadaDto {
  id: number;
  descricao: string;
  perfilId: number;
  tipoAlcada: TipoAlcadaCompra;
  valorLimite?: number;
  isAtivo: boolean;
  ordem: number;
  dtCriacao: string;
}

export interface CriarAlcadaRequest {
  descricao: string;
  perfilId: number;
  tipoAlcada: TipoAlcadaCompra;
  valorLimite?: number;
  ordem: number;
}

export interface AtualizarAlcadaRequest extends CriarAlcadaRequest {
  isAtivo: boolean;
}

// ── Cotação / RFQ (Request for Quotation) ────────────────────────────────

export type StatusRfq =
  | 'Rascunho'
  | 'Enviada'
  | 'EmResposta'
  | 'Encerrada'
  | 'Adjudicada'
  | 'Cancelada';

export interface RfqItem {
  id: number;
  produtoId: number;
  descricaoComplementar?: string;
  quantidade: number;
  unidadeId?: number;
  custoEstimado: number;
}

export interface RfqFornecedorItem {
  id: number;
  rfqItemId: number;
  produtoId: number;
  custoUnitario: number;
  desconto: number;
  totalItem: number;
  observacao?: string;
}

export interface RfqFornecedor {
  id: number;
  fornecedorId: number;
  nomeFornecedor?: string;
  respondeu: boolean;
  isVencedor: boolean;
  dtConvite?: string;
  dtResposta?: string;
  valorFrete: number;
  prazoEntregaDias?: number;
  prazoPagamentoDias?: number;
  valorTotalProposta: number;
  observacao?: string;
  itens: RfqFornecedorItem[];
}

export interface RfqResumo {
  id: number;
  numero: number;
  titulo: string;
  status: StatusRfq;
  dtEmissao: string;
  dtPrazo?: string;
  totalFornecedores: number;
  totalResponderam: number;
  requisicaoOrigemId?: number;
  ordemGeradaId?: number;
  dtCriacao: string;
}

export interface RfqDetalhe extends RfqResumo {
  descricao?: string;
  fornecedorVencedorId?: number;
  itens: RfqItem[];
  fornecedores: RfqFornecedor[];
}

export interface RfqEnvioEmails {
  enviados: number;
  total: number;
  falhas: string[];
}

export interface EnviarRfqResponse {
  rfq: RfqResumo;
  emails: RfqEnvioEmails;
}

export interface RfqItemRequest {
  produtoId: number;
  descricaoComplementar?: string;
  quantidade: number;
  unidadeId?: number;
  custoEstimado: number;
}

export interface RfqFornecedorRequest {
  fornecedorId: number;
}

export interface CriarRfqRequest {
  titulo: string;
  descricao?: string;
  dtPrazo?: string;
  requisicaoOrigemId?: number;
  itens: RfqItemRequest[];
  fornecedores: RfqFornecedorRequest[];
}

export interface AtualizarRfqRequest {
  titulo: string;
  descricao?: string;
  dtPrazo?: string;
  itens: RfqItemRequest[];
  fornecedores: RfqFornecedorRequest[];
}

export interface RfqFornecedorItemRequest {
  rfqItemId: number;
  produtoId: number;
  custoUnitario: number;
  desconto: number;
  observacao?: string;
}

export interface RegistrarRespostaFornecedorRequest {
  valorFrete: number;
  prazoEntregaDias?: number;
  prazoPagamentoDias?: number;
  observacao?: string;
  itens: RfqFornecedorItemRequest[];
}

export interface AdjudicarRfqRequest {
  fornecedorVencedorId: number;
  gerarOrdemCompra: boolean;
  localEstoqueId?: number;
  prazoPagamentoDias: number;
}

// ── Histórico de Preços ───────────────────────────────────────────────────

export interface HistoricoPrecoDto {
  id: number;
  produtoId: number;
  fornecedorId: number;
  nomeFornecedor?: string;
  recebimentoId?: number;
  custoUnitario: number;
  quantidade: number;
  numeroNf?: string;
  dtReferencia: string;
  dtCriacao: string;
}

export interface ProcessarRecebimentoRequest {
  dtRecebimento: string;
  localEstoqueId: number;
  numeroNf?: string;
  serieNf?: string;
  chaveNfe?: string;
  dtEmissaoNf?: string;
  valorNf: number;
  observacao?: string;
  centroCustoId: number;
  contaContabilId: number;
  tipoDespesasId: number;
  grupoDespesasId: number;
  itens: RecebimentoCompraItemRequest[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Recebimento com XML NF-e — staging (iteração 1: apenas parse)
// ══════════════════════════════════════════════════════════════════════════════

export type ValidacaoSeveridade = 'Info' | 'Aviso' | 'Erro';

export interface Validacao {
  severidade: ValidacaoSeveridade;
  codigo: string;
  mensagem: string;
}

export interface NfeEndereco {
  logradouro: string;
  numero?: string;
  complemento?: string;
  bairro: string;
  codigoMunicipio?: string;
  municipio: string;
  uf: string;
  cep?: string;
  fone?: string;
}

export interface NfeIdent {
  chaveAcesso: string;
  numero: number;
  serie: number;
  dtEmissao: string;
  dtEntradaSaida: string;
  modelo: number;
  tipoOperacao: number;
  finalidadeEmissao: number;
  naturezaOperacao: string;
  codigoMunicipioFato?: string;
  ufEmitente?: string;
  informacoesAdicionais?: string;
}

export interface NfeEmitente {
  cnpjCpf: string;
  razaoSocial: string;
  nomeFantasia?: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal?: string;
  codigoCnae?: string;
  regimeTributario?: number;
  endereco?: NfeEndereco;
}

export interface NfeDestinatario {
  cnpjCpf: string;
  razaoSocial: string;
  inscricaoEstadual?: string;
  email?: string;
  endereco?: NfeEndereco;
}

export interface IcmsItem {
  origemMercadoria: number;
  cst: string;
  modalidadeBaseCalculo?: number;
  baseCalculo: number;
  aliquota: number;
  valor: number;
  percentualReducaoBC?: number;
  valorDesonerado?: number;
  motivoDesoneracao?: number;
  modalidadeBaseCalculoSt?: number;
  percentualMva?: number;
  percentualReducaoBCSt?: number;
  baseCalculoSt?: number;
  aliquotaSt?: number;
  valorSt?: number;
  valorIcmsSubstituidoAntes?: number;
  baseCalculoFcp?: number;
  aliquotaFcp?: number;
  valorFcp?: number;
  baseCalculoFcpSt?: number;
  aliquotaFcpSt?: number;
  valorFcpSt?: number;
  valorFcpRetidoAnterior?: number;
  baseCalculoUfDestino?: number;
  baseCalculoFcpUfDestino?: number;
  aliquotaFcpUfDestino?: number;
  aliquotaInterestadual?: number;
  percentualPartilhaUfDest?: number;
  valorIcmsUfDestino?: number;
  valorIcmsUfRemetente?: number;
  valorFcpUfDestino?: number;
}

export interface IpiItem {
  cst: string;
  baseCalculo: number;
  aliquota: number;
  valor: number;
}

export interface PisCofinsItem {
  cst: string;
  baseCalculo: number;
  aliquota: number;
  valor: number;
}

export interface ImpostosItem {
  icms?: IcmsItem;
  ipi?: IpiItem;
  pis?: PisCofinsItem;
  cofins?: PisCofinsItem;
  valorAproximadoTributos?: number;
}

export interface ItemStaging {
  numeroItem: number;
  codigoProdutoFornecedor: string;
  gtin?: string;
  descricao: string;
  ncm: string;
  cest?: string;
  cfop: string;
  unidade: string;
  quantidade: number;
  valorUnitario: number;
  valorTotalBruto: number;
  desconto: number;
  frete: number;
  seguro: number;
  outrasDespesas: number;
  compoeTotal: boolean;
  pedidoCompraXml?: string;
  itemPedidoCompraXml?: number;
  informacoesAdicionaisItem?: string;
  impostos: ImpostosItem;
  descontoRateado: number;
  freteRateado: number;
  seguroRateado: number;
  outrasDespesasRateadas: number;
  valorLiquido: number;
}

export interface NfeTotais {
  baseCalculoIcms: number;
  valorIcms: number;
  valorIcmsDesonerado: number;
  baseCalculoIcmsSt: number;
  valorIcmsSt: number;
  valorFcp: number;
  valorFcpSt: number;
  valorFcpRetidoAnterior: number;
  valorProdutos: number;
  valorFrete: number;
  valorSeguro: number;
  valorDesconto: number;
  valorOutrasDespesas: number;
  valorIpi: number;
  valorIpiDevolvido: number;
  valorPis: number;
  valorCofins: number;
  valorImportacao: number;
  valorTotal: number;
  valorAproximadoTributos: number;
}

export interface Transportadora {
  cnpjCpf: string;
  razaoSocial?: string;
  inscricaoEstadual?: string;
  endereco?: string;
  municipio?: string;
  uf?: string;
}

export interface NfeTransporte {
  modalidadeFrete: number;
  transportadora?: Transportadora;
  placa?: string;
  ufPlaca?: string;
  rntc?: string;
  quantidadeVolumes?: number;
  especieVolumes?: string;
  pesoBruto?: number;
  pesoLiquido?: number;
}

export interface Parcela {
  numero: string;
  dataVencimento: string;
  valor: number;
}

export interface RecebimentoStaging {
  identificacao: NfeIdent;
  emitente: NfeEmitente;
  destinatario: NfeDestinatario;
  itens: ItemStaging[];
  totais: NfeTotais;
  transporte?: NfeTransporte;
  parcelas: Parcela[];
  validacoes: Validacao[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Recebimento com XML — preview (iteração 2: matching com banco + pendências)
// ══════════════════════════════════════════════════════════════════════════════

export type FornecedorMatchStatus =
  | 'Encontrado'
  | 'EncontradoNaoFornecedor'
  | 'NaoEncontrado';

export interface FornecedorMatch {
  status: FornecedorMatchStatus;
  fornecedorId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  inscricaoEstadual?: string;
}

export type ProdutoMatchStatus = 'Encontrado' | 'NaoEncontrado' | 'MultiplosCandidatos';

export type ProdutoMatchMetodo = 'CodigoFornecedor' | 'Gtin' | 'CodigoBarras' | 'Descricao';

export interface ProdutoMatch {
  status: ProdutoMatchStatus;
  produtoId?: number;
  descricao?: string;
  gtin?: string;
  codigoBarras?: string;
  fatorConversaoEntrada?: number;
  metodoMatch?: ProdutoMatchMetodo;
}

export interface CompraOrdemItemMatch {
  compraOrdemItemId: number;
  produtoId: number;
  quantidadePedida: number;
  quantidadeRecebidaAnterior: number;
  quantidadeDisponivel: number;
  custoUnitarioPedido: number;
}

export type PendenciaTipo =
  | 'ProdutoSemVinculo'
  | 'PoItemNaoEncontrado'
  | 'QtdDivergenteDoPedido'
  | 'CustoDivergenteDoPedido';

export interface Pendencia {
  tipo: PendenciaTipo;
  mensagem: string;
}

export type ItemPreviewSemaforo = 'Verde' | 'Amarelo' | 'Vermelho';

export interface ItemPreview {
  xml: ItemStaging;
  produto: ProdutoMatch;
  poItem?: CompraOrdemItemMatch;
  pendencias: Pendencia[];
  semaforo: ItemPreviewSemaforo;
}

export interface CompraOrdemResumo {
  id: number;
  numero: number;
  fornecedorNome: string;
  status: string;
  valorTotal: number;
}

export interface PreviewResumo {
  totalItens: number;
  itensComMatch: number;
  itensSemMatch: number;
  itensDivergentes: number;
  pendenciasBloqueantes: number;
  podeConfirmar: boolean;
}

export interface RecebimentoPreview {
  staging: RecebimentoStaging;
  fornecedor: FornecedorMatch;
  itens: ItemPreview[];
  ordemVinculada?: CompraOrdemResumo;
  resumo: PreviewResumo;
}

// ══════════════════════════════════════════════════════════════════════════════
//  Recebimento — confirmação (iteração 4: persistência transacional)
// ══════════════════════════════════════════════════════════════════════════════

export interface ItemConfirmadoRequest {
  numeroItem: number;
  produtoId: number;
  compraOrdemItemId?: number;
  quantidadeRecebida: number;
  localEstoqueId: number;
  lote?: string;
  dtValidade?: string;
  /** Unidades de estoque por unidade do XML (caixa com 12 → 12). Ausente = 1. */
  fatorConversao?: number;
}

export interface ParcelaConfirmadaRequest {
  numero: string;
  dataVencimento: string;
  valor: number;
  formaPagRecId?: number;
}

export interface ConfirmarRecebimentoRequest {
  base64Xml: string;
  compraOrdemId?: number;
  fornecedorId: number;
  localEstoqueId: number;
  usuarioId: number;
  itens: ItemConfirmadoRequest[];
  parcelas?: ParcelaConfirmadaRequest[];
  prazoPagamentoDiasSeAvulso?: number;
  formaPagRecId?: number;
  centroCustoId: number;
  contaContabilId: number;
  tipoDespesasId: number;
  grupoDespesasId: number;
  observacao?: string;
}

export interface RecebimentoConfirmado {
  id: number;
  numero: number;
  fornecedorId: number;
  chaveNfe?: string;
  numeroNf?: string;
  valorTotal: number;
  contaPagarId?: number;
  itensCriados: number;
  parcelasCriadas: number;
  dtRecebimento: string;
}

// ══════════════════════════════════════════════════════════════════════════════
//  Ajuste de preço pós-recebimento (iteração 5)
// ══════════════════════════════════════════════════════════════════════════════

export interface ConfigCalculo {
  icmsCompeCusto: boolean;
  icmsStCompeCusto: boolean;
  ipiCompeCusto: boolean;
  pisCofinsCompeCusto: boolean;
  freteCompeCusto: boolean;
  seguroCompeCusto: boolean;
  outrosCompeCusto: boolean;
  percentualCustoOperacional: number;
  percentualMargemPadrao: number;
}

export interface AjustePrecoLinha {
  numeroItem: number;
  produtoId: number;
  descricaoProduto: string;
  gtin?: string;
  unidade: string;
  quantidade: number;
  valorUnitarioXml: number;
  custoBrutoItem: number;
  icmsCompoeCusto: number;
  icmsStCompoeCusto: number;
  ipiCompoeCusto: number;
  pisCofinsCompoeCusto: number;
  freteRateado: number;
  seguroRateado: number;
  outrosRateados: number;
  descontoRateado: number;
  totalImpostosAgregados: number;
  custoComImpostos: number;
  acrescimoOperacional: number;
  custoReposicao: number;
  custoReposicaoUnitario: number;
  precoAtual: number;
  margemAtualPercent: number;
  precoSugerido: number;
  percentualMargemAplicada: number;
}

export interface AjustePrecoPreview {
  recebimentoId: number;
  recebimentoNumero: number;
  chaveNfe?: string;
  config: ConfigCalculo;
  itens: AjustePrecoLinha[];
}

export interface AplicarAjustePrecoItemRequest {
  produtoId: number;
  precoNovo: number;
  custoReposicao?: number;
  percentualMargemAplicada?: number;
  percentualCustoOperacional?: number;
}

export interface AplicarAjustePrecoRequest {
  usuarioId: number;
  itens: AplicarAjustePrecoItemRequest[];
  observacao?: string;
}

export interface AjusteAplicadoDetalhe {
  produtoId: number;
  descricao: string;
  precoAnterior: number;
  precoNovo: number;
}

export interface AplicarAjustePrecoResultado {
  recebimentoId: number;
  produtosAtualizados: number;
  detalhes: AjusteAplicadoDetalhe[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Manifestação DFe (iteração 6)
// ══════════════════════════════════════════════════════════════════════════════

export type TipoManifestacaoDfe =
  | 'Ciencia'
  | 'Confirmacao'
  | 'Desconhecimento'
  | 'OperacaoNaoRealizada';

export interface ManifestarDfeRequest {
  tipo: TipoManifestacaoDfe;
  usuarioId: number;
  justificativa?: string;
}

export interface ManifestacaoResultado {
  id: number;
  tipoEvento: number;
  tipoEventoDescricao: string;
  chaveNfe?: string;
  sucesso: boolean;
  cStat: number;
  xMotivo: string;
  protocolo: number;
  dtEvento: string;
  xmlEnvio?: string;
  xmlRetorno?: string;
}

export interface ManifestacaoHistoricoItem {
  id: number;
  tipoEvento: number;
  tipoEventoDescricao: string;
  codigoSituacao: number;
  descricaoSituacao?: string;
  protocolo: number;
  dtEvento: string;
  usuarioId: number;
}

// ══════════════════════════════════════════════════════════════════════════════
//  Listagem e detalhe de recebimentos (iteração 7)
// ══════════════════════════════════════════════════════════════════════════════

export interface RecebimentoListagem {
  id: number;
  numero: number;
  dtRecebimento: string;
  fornecedorId: number;
  fornecedorNome?: string;
  ordemId?: number;
  ordemNumero?: number;
  numeroNf?: string;
  serieNf?: string;
  chaveNfe?: string;
  dtEmissaoNf?: string;
  valorNf: number;
  valorTotal: number;
  status: string;
  isThreeWayMatch: boolean;
  contaPagarGeradaId?: number;
  quantidadeItens: number;
}

export interface RecebimentoItemDetalhe {
  id: number;
  produtoId: number;
  descricaoProduto?: string;
  codigoFornecedor?: string;
  gtin?: string;
  quantidadeRecebida: number;
  custoUnitario: number;
  totalItem: number;
  localEstoqueId?: number;
  lote?: string;
  dtValidade?: string;
}

// ══════════════════════════════════════════════════════════════════════════════
//  Fluxo de Caixa Compras (Fase 3 · Iter 3.1)
// ══════════════════════════════════════════════════════════════════════════════

export type AgrupamentoFluxoCaixa = 'Dia' | 'Semana' | 'Mes';

export interface FluxoCaixaComprasResumo {
  totalParcelas: number;
  totalGeral: number;
  totalVencido: number;
  totalAVencer7d: number;
  totalAVencer30d: number;
  totalPago: number;
  qtdFornecedores: number;
}

export interface FluxoCaixaPeriodo {
  dataReferencia: string;
  rotuloPeriodo: string;
  quantidadeParcelas: number;
  valorTotal: number;
  valorVencido: number;
  valorAberto: number;
  valorPago: number;
}

export interface FluxoCaixaPorFornecedor {
  fornecedorId: number;
  fornecedorNome?: string;
  quantidadeParcelas: number;
  valorTotal: number;
  valorAberto: number;
}

export interface FluxoCaixaParcela {
  contaPagarBaixaId: number;
  contaPagarId: number;
  recebimentoId: number;
  recebimentoNumero: number;
  fornecedorId: number;
  fornecedorNome?: string;
  numeroNf?: string;
  chaveNfe?: string;
  nroParcela: number;
  qtdParcelasTotal: number;
  dtEmissao: string;
  dtVencimento: string;
  valor: number;
  isPago: boolean;
  dtPagamento?: string;
  situacao: string;
}

export interface FluxoCaixaComprasResponse {
  resumo: FluxoCaixaComprasResumo;
  seriePorPeriodo: FluxoCaixaPeriodo[];
  topFornecedores: FluxoCaixaPorFornecedor[];
  parcelas: FluxoCaixaParcela[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Spend Analytics (Fase 3 · Iter 3.2)
// ══════════════════════════════════════════════════════════════════════════════

export interface SpendResumo {
  totalGasto: number;
  totalRecebimentos: number;
  totalItens: number;
  qtdFornecedoresUnicos: number;
  qtdProdutosUnicos: number;
  ticketMedioRecebimento: number;
  gastoMedioMensal: number;
}

export interface SpendMes {
  ano: number;
  mes: number;
  rotuloMes: string;
  valorGasto: number;
  qtdRecebimentos: number;
}

export interface SpendParetoFornecedor {
  posicao: number;
  fornecedorId: number;
  fornecedorNome?: string;
  valorGasto: number;
  percentualDoTotal: number;
  percentualAcumulado: number;
  qtdRecebimentos: number;
  isCorte80: boolean;
}

export interface SpendParetoProduto {
  posicao: number;
  produtoId: number;
  descricao?: string;
  grupoId: number;
  grupoNome?: string;
  valorGasto: number;
  percentualDoTotal: number;
  percentualAcumulado: number;
  quantidadeTotal: number;
  isCorte80: boolean;
}

export interface SpendGrupo {
  grupoId: number;
  grupoNome?: string;
  valorGasto: number;
  percentualDoTotal: number;
  qtdProdutosDistintos: number;
}

export interface SpendAnalyticsResponse {
  resumo: SpendResumo;
  seriePorMes: SpendMes[];
  topFornecedoresPareto: SpendParetoFornecedor[];
  topProdutosPareto: SpendParetoProduto[];
  gastoPorGrupo: SpendGrupo[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Performance de Fornecedores · OTIF (Fase 3 · Iter 3.3)
// ══════════════════════════════════════════════════════════════════════════════

export interface PerformanceResumo {
  totalPOs: number;
  pOsCompletadas: number;
  otifGlobalPercent: number;
  onTimePercent: number;
  inFullPercent: number;
  leadTimeMedioDias: number;
  divergenciaCustoMediaPercent: number;
  qtdFornecedores: number;
}

export interface PerformanceFornecedor {
  fornecedorId: number;
  fornecedorNome?: string;
  totalPOs: number;
  pOsCompletadas: number;
  pOsOnTime: number;
  pOsInFull: number;
  pOsOtif: number;
  onTimePercent: number;
  inFullPercent: number;
  otifPercent: number;
  leadTimeMedioDias: number;
  divergenciaCustoMediaPercent: number;
  score: number;
  classificacao: string;
}

export interface PerformanceMensal {
  ano: number;
  mes: number;
  rotuloMes: string;
  totalPOs: number;
  pOsOtif: number;
  otifPercent: number;
  leadTimeMedioDias: number;
}

export interface PerformanceFornecedoresResponse {
  resumo: PerformanceResumo;
  top: PerformanceFornecedor[];
  bottom: PerformanceFornecedor[];
  otifMensal: PerformanceMensal[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Relatório de POs (Fase 3 · Iter 3.3)
// ══════════════════════════════════════════════════════════════════════════════

export interface RelatorioPosResumo {
  totalPOs: number;
  valorTotalPedidos: number;
  pOsAtrasadas: number;
  pOsCompletamenteRecebidas: number;
  pOsParcialmenteRecebidas: number;
  pOsCanceladas: number;
  pOsComThreeWayMatch: number;
  divergenciaMediaPrecoPercent: number;
}

export interface RelatorioPoItem {
  pedidoId: number;
  numero: number;
  fornecedorId: number;
  fornecedorNome?: string;
  dtEmissao: string;
  dtEntregaPrevista?: string;
  dtPrimeiroRecebimento?: string;
  valorPedido: number;
  valorRecebido: number;
  status: string;
  diasAtraso?: number;
  isAtrasado: boolean;
  qtdItens: number;
  qtdRecebimentos: number;
  temThreeWayMatch: boolean;
  divergenciaPrecoPercent: number;
}

export interface RelatorioPosStatus {
  status: string;
  quantidade: number;
  valorTotal: number;
}

export interface RelatorioPosResponse {
  resumo: RelatorioPosResumo;
  pedidos: RelatorioPoItem[];
  distribuicaoPorStatus: RelatorioPosStatus[];
  topDivergenciaPreco: RelatorioPoItem[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Budget / Orçamento de Compras (Fase 3 · Iter 3.4)
// ══════════════════════════════════════════════════════════════════════════════

export interface CompraBudget {
  id: number;
  ano: number;
  mes: number;
  grupoProdutoId: number;
  grupoProdutoNome?: string;
  valorPlanejado: number;
  observacao?: string;
  dtUltimaAtualizacao: string;
  usuarioId: number;
}

export interface SalvarCompraBudgetRequest {
  ano: number;
  mes: number;
  grupoProdutoId: number;
  valorPlanejado: number;
  observacao?: string;
}

export type BudgetStatus =
  | 'SemOrcamento'
  | 'DentroDoPlanejado'
  | 'Atencao'
  | 'Estourado';

export interface BudgetResumo {
  ano: number;
  totalPlanejado: number;
  totalRealizado: number;
  saldoDisponivel: number;
  percentualExecutado: number;
  qtdGruposOrcados: number;
  qtdGruposEstourados: number;
}

export interface BudgetMes {
  mes: number;
  rotuloMes: string;
  valorPlanejado: number;
  valorRealizado: number;
  saldo: number;
  percentualExecutado: number;
}

export interface BudgetLinha {
  grupoProdutoId: number;
  grupoProdutoNome?: string;
  valorPlanejado: number;
  valorRealizado: number;
  saldo: number;
  percentualExecutado: number;
  status: BudgetStatus;
}

export interface BudgetVsRealizadoResponse {
  resumo: BudgetResumo;
  seriePorMes: BudgetMes[];
  linhasPorGrupo: BudgetLinha[];
}

// ══════════════════════════════════════════════════════════════════════════════
//  Download XML SEFAZ (stub)
// ══════════════════════════════════════════════════════════════════════════════

export interface BaixarXmlSefazResultado {
  sucesso: boolean;
  chaveNfe?: string;
  xmlBase64?: string;
  cStat: number;
  xMotivo: string;
}

export interface RecebimentoDetalhe {
  id: number;
  numero: number;
  dtRecebimento: string;
  fornecedorId: number;
  fornecedorNome?: string;
  fornecedorCpfCnpj?: string;
  ordemId?: number;
  ordemNumero?: number;
  localEstoqueId: number;
  numeroNf?: string;
  serieNf?: string;
  chaveNfe?: string;
  dtEmissaoNf?: string;
  valorNf: number;
  valorTotal: number;
  status: string;
  isThreeWayMatch: boolean;
  dtThreeWayMatch?: string;
  contaPagarGeradaId?: number;
  observacao?: string;
  temXmlOriginal: boolean;
  dtUltimaAtualizacao: string;
  usuarioId: number;
  itens: RecebimentoItemDetalhe[];
}
